import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../utils/httpClient';
import type { TMdmAccount, TMdmPolicyTemplates, TMdmDevice } from "@nucleus-care/nucleuscare-data-types";

export const fetchMdmAccounts = createAsyncThunk<TMdmAccount[]>(
  'mdmAccounts/data/fetchMdmAccounts',
  async () => {
    const { data } = await api.get('/mdm/accounts') as { data: { mdmAccounts: TMdmAccount[], ok: boolean } }
    return data.mdmAccounts
  }
);

export const fetchMdmAccountsDevicesCount = createAsyncThunk<{ mdmAccountId: string; mdmAccountName: string; deviceCount: number }[]>(
  'mdmAccounts/data/fetchMdmAccountsDevicesCount',
  async () => {
    const { data } = await api.post('/mdm/accounts/get_devices') as { 
      data: { mdmAccountsDeviceCounts: { mdmAccountId: string; mdmAccountName: string; deviceCount: number }[] }
    };
    return data.mdmAccountsDeviceCounts;
  }
);

export const createMdmAccount = createAsyncThunk<string, TMdmAccount>(
  'mdmAccounts/data/createMdmAccount',
  async (mdmAccountForm: TMdmAccount) => {
    const { data } = await api.post('/mdm/accounts', mdmAccountForm) as { data: { mdmAccountId: string, ok: boolean } }
    return data.mdmAccountId
  }
);

export const patchMdmAccount = createAsyncThunk<string, { mdmAccountId: string, mdmAccountData: TMdmAccount }>(
  'mdmAccounts/data/patchMdmAccount',
  async ({ mdmAccountId, mdmAccountData }: { mdmAccountId: string, mdmAccountData: TMdmAccount }) => {
    const { data } = await api.patch(`/mdm/accounts/${mdmAccountId}`, mdmAccountData)
    return data.mdmAccountId
  }
);
export const fetchMdmAccountByID = createAsyncThunk<TMdmAccount, string>(
  'mdmAccounts/data/fetchMdmAccountByID',
  async (mdmAccountId: string) => {
    const { data } = await api.get(`/mdm/accounts/${mdmAccountId}`)
    return data.mdmAccount
  }
);

export const fetchMdmAccountPolicyTemplates = createAsyncThunk<TMdmPolicyTemplates[], string>(
  'mdmAccounts/data/fetchMdmAccountPolicyTemplates',
  async (mdmAccountId: string) => {
    const { data } = await api.get(`/mdm/accounts/${mdmAccountId}/policy_templates`)
    return data.policyTemplates
  }
);

export const assignPolicyTemplatesToMdmAccount = createAsyncThunk<string[], { mdmAccountId: string, MdmPolicyTemplates: TMdmPolicyTemplates[] }>(
  'mdmAccounts/data/assignPolicyTemplatesToMdmAccount',
  async ({ mdmAccountId, MdmPolicyTemplates }: { mdmAccountId: string, MdmPolicyTemplates: TMdmPolicyTemplates[] }) => {
    const { data } = await api.post(`mdm/accounts/${mdmAccountId}/policy_templates`, { MdmPolicyTemplates })
    return data.mdmPolicyTemplates as string[]
  }
);

export const fetchMdmAccountDevices = createAsyncThunk<TMdmDevice[], string>(
  'mdmAccounts/data/fetchMdmAccountDevices',
  async (mdmAccountId: string) => {
    const { data } = await api.get(`/mdm/accounts/${mdmAccountId}/devices`)
    return data.Devices as TMdmDevice[]
  }
);

export const handleRenewEnrollmentToken = createAsyncThunk<boolean, string>(
  'mdmAccounts/data/handleRenewEnrollmentToken',
  async (mdmAccountId: string) => {
    const { data } = await api.post(`/mdm/accounts/${mdmAccountId}/enrollmentToken/renew`)
    return data.ok
  }
);

const initialState = {
  fetchAccounts: {
    loading: false,
    success: false,
    mdmAccounts: [] as TMdmAccount[],
    error: false,
  },
  fetchMdmAccountsDevicesCountState: {
    loading: false,
    success: false,
    error: false,
    mdmAccountsDeviceCounts: [] as { mdmAccountId: string; deviceCount: number }[]
  },
  createMdmAccount: {
    loading: false,
    success: false,
    error: false,
    newMdmAccountID: null as string | null
  },
  patchMdmAccount: {
    loading: false,
    success: false,
    error: false,
    patchMdmAccountID: null as string | null
  },
  fetchAccountInformation: {
    loading: false,
    success: false,
    error: false,
    mdmAccount: null as null | TMdmAccount
  },
  fetchAccountPolicyTemplates: {
    loading: false,
    success: false,
    error: false,
    policyTemplates: [] as TMdmPolicyTemplates[]
  },
  fetchMdmAccountDevices: {
    loading: false,
    success: false,
    error: false,
    devices: [] as TMdmDevice[]
  },
  assignPolicyTemplatesToMdmAccount: {
    loading: false,
    success: false,
    error: false,
    mdmPolicyTemplates: [] as string[]
  },
  handleRenewEnrollmentTokenState: {
    loading: false,
    success: false,
    error: false,
    ok: false
  }
}

const mdmAccountsDataSlice = createSlice({
  name: "mdmAccounts/data",
  initialState: initialState,
  reducers: {
    resetFetchAccounts: (state) => {
      state.fetchAccounts = initialState.fetchAccounts
    },
    resetFetchMdmAccountsDevicesCountState: (state) => {
      state.fetchMdmAccountsDevicesCountState = initialState.fetchMdmAccountsDevicesCountState
    },
    resetCreateAccount: (state) => {
      state.createMdmAccount = initialState.createMdmAccount
    },
    resetPatchAccount: (state) => {
      state.patchMdmAccount = initialState.patchMdmAccount
    },
    resetFetchAccountInformationByID: (state) => {
      state.fetchAccountInformation = initialState.fetchAccountInformation
    },
    resetFetchAccountPolicyTemplates: (state) => {
      state.fetchAccountPolicyTemplates = initialState.fetchAccountPolicyTemplates
    },
    resetFetchMdmAccountDevices: (state) => {
      state.fetchMdmAccountDevices = initialState.fetchMdmAccountDevices
    },
    resetAssignPolicyTemplatesToMdmAccount: (state) => {
      state.assignPolicyTemplatesToMdmAccount = initialState.assignPolicyTemplatesToMdmAccount
    },
    resetHandleCreateOrRenewEnrollmentTokenState: (state) => {
      state.handleRenewEnrollmentTokenState = initialState.handleRenewEnrollmentTokenState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMdmAccounts.pending, (state) => {
        state.fetchAccounts = {
          loading: true,
          success: false,
          mdmAccounts: [],
          error: false,
        }
      })
      .addCase(fetchMdmAccounts.fulfilled, (state, action) => {
        state.fetchAccounts = {
          loading: false,
          success: true,
          mdmAccounts: action.payload,
          error: false,
        }
      })
      .addCase(fetchMdmAccounts.rejected, (state) => {
        state.fetchAccounts = {
          loading: false,
          success: false,
          mdmAccounts: [],
          error: true,
        }
      })
    builder
      .addCase(fetchMdmAccountsDevicesCount.pending, (state) => {
        state.fetchMdmAccountsDevicesCountState = {
          loading: true,
          success: false,
          error: false,
          mdmAccountsDeviceCounts: [] as { mdmAccountId: string; mdmAccountName: string; deviceCount: number }[]
        }
      })
      .addCase(fetchMdmAccountsDevicesCount.fulfilled, (state, action) => {
        state.fetchMdmAccountsDevicesCountState = {
          loading: false,
          success: true,
          error: false,
          mdmAccountsDeviceCounts: action.payload
        }
      })
      .addCase(fetchMdmAccountsDevicesCount.rejected, (state) => {
        state.fetchMdmAccountsDevicesCountState = {
          loading: false,
          success: false,
          error: true,
          mdmAccountsDeviceCounts: [] as { mdmAccountId: string; mdmAccountName: string; deviceCount: number }[]
        }
      })
    builder
      .addCase(createMdmAccount.pending, (state) => {
        state.createMdmAccount = {
          loading: true,
          success: false,
          error: false,
          newMdmAccountID: null
        };
      })
      .addCase(createMdmAccount.fulfilled, (state, action) => {
        state.createMdmAccount = {
          loading: false,
          success: true,
          error: false,
          newMdmAccountID: action.payload
        };
      })
      .addCase(createMdmAccount.rejected, (state) => {
        state.createMdmAccount = {
          loading: false,
          success: false,
          error: true,
          newMdmAccountID: null
        };
      });
    builder
      .addCase(patchMdmAccount.pending, (state) => {
        state.patchMdmAccount = {
          loading: true,
          success: false,
          error: false,
          patchMdmAccountID: null
        };
      })
      .addCase(patchMdmAccount.fulfilled, (state, action) => {
        state.patchMdmAccount = {
          loading: false,
          success: true,
          error: false,
          patchMdmAccountID: action.payload
        };
      })
      .addCase(patchMdmAccount.rejected, (state) => {
        state.patchMdmAccount = {
          loading: false,
          success: false,
          error: true,
          patchMdmAccountID: null
        };
      });
    builder
      .addCase(fetchMdmAccountByID.pending, (state) => {
        state.fetchAccountInformation = {
          loading: true,
          success: false,
          error: false,
          mdmAccount: null
        };
      })
      .addCase(fetchMdmAccountByID.fulfilled, (state, action) => {
        state.fetchAccountInformation = {
          loading: false,
          success: true,
          error: false,
          mdmAccount: action.payload
        };
      })
      .addCase(fetchMdmAccountByID.rejected, (state) => {
        state.fetchAccountInformation = {
          loading: false,
          success: false,
          error: true,
          mdmAccount: null
        };
      });
    builder
      .addCase(fetchMdmAccountPolicyTemplates.pending, (state) => {
        state.fetchAccountPolicyTemplates = {
          loading: false,
          success: false,
          error: false,
          policyTemplates: [] as TMdmPolicyTemplates[]
        }
      })
      .addCase(fetchMdmAccountPolicyTemplates.fulfilled, (state, action) => {
        state.fetchAccountPolicyTemplates = {
          loading: false,
          success: true,
          error: false,
          policyTemplates: action.payload
        };
      })
      .addCase(fetchMdmAccountPolicyTemplates.rejected, (state) => {
        state.fetchAccountPolicyTemplates = {
          loading: false,
          success: false,
          error: true,
          policyTemplates: [] as TMdmPolicyTemplates[]
        };
      });
    builder
      .addCase(fetchMdmAccountDevices.pending, (state) => {
        state.fetchMdmAccountDevices = {
          loading: false,
          success: false,
          error: false,
          devices: [] as TMdmDevice[]
        }
      })
      .addCase(fetchMdmAccountDevices.fulfilled, (state, action) => {
        state.fetchMdmAccountDevices = {
          loading: false,
          success: true,
          error: false,
          devices: action.payload
        };
      })
      .addCase(fetchMdmAccountDevices.rejected, (state) => {
        state.fetchMdmAccountDevices = {
          loading: false,
          success: false,
          error: true,
          devices: [] as TMdmDevice[]
        };
      });
    builder
      .addCase(assignPolicyTemplatesToMdmAccount.pending, (state) => {
        state.assignPolicyTemplatesToMdmAccount = {
          loading: true,
          success: false,
          error: false,
          mdmPolicyTemplates: []
        }
      })
      .addCase(assignPolicyTemplatesToMdmAccount.fulfilled, (state, action) => {
        state.assignPolicyTemplatesToMdmAccount = {
          loading: false,
          success: true,
          error: false,
          mdmPolicyTemplates: action.payload
        };
      })
      .addCase(assignPolicyTemplatesToMdmAccount.rejected, (state) => {
        state.assignPolicyTemplatesToMdmAccount = {
          loading: false,
          success: false,
          error: true,
          mdmPolicyTemplates: []
        };
      });
    builder
      .addCase(handleRenewEnrollmentToken.pending, (state) => {
        state.handleRenewEnrollmentTokenState = {
          loading: false,
          success: false,
          error: false,
          ok: false
        }
      })
      .addCase(handleRenewEnrollmentToken.fulfilled, (state, action) => {
        state.handleRenewEnrollmentTokenState = {
          loading: false,
          success: true,
          error: false,
          ok: action.payload
        };
      })
      .addCase(handleRenewEnrollmentToken.rejected, (state) => {
        state.handleRenewEnrollmentTokenState = {
          loading: false,
          success: false,
          error: true,
          ok: false
        };
      });
  },
});
export const {
  resetFetchAccounts,
  resetFetchMdmAccountsDevicesCountState,
  resetCreateAccount,
  resetPatchAccount,
  resetFetchAccountInformationByID,
  resetFetchAccountPolicyTemplates,
  resetFetchMdmAccountDevices,
  resetAssignPolicyTemplatesToMdmAccount,
  resetHandleCreateOrRenewEnrollmentTokenState
} = mdmAccountsDataSlice.actions

export default mdmAccountsDataSlice.reducer;